import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceDeadlines = ({ deadlines = [] }) => {
  const [filter, setFilter] = useState('all'); // all, overdue, pending, completed
  const [sortBy, setSortBy] = useState('deadline'); // deadline, priority, status

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilDeadline = (deadlineString) => {
    const deadline = new Date(deadlineString);
    const today = new Date();
    const diffTime = deadline?.getTime() - today?.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'in-progress': return 'text-primary';
      case 'draft': return 'text-warning';
      case 'overdue': return 'text-error';
      case 'not-started': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBackground = (status) => {
    switch (status) {
      case 'completed': return 'bg-success/10';
      case 'in-progress': return 'bg-primary/10';
      case 'draft': return 'bg-warning/10';
      case 'overdue': return 'bg-error/10';
      case 'not-started': return 'bg-muted/10';
      default: return 'bg-muted/10';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-error';
      case 'high': return 'text-warning';
      case 'medium': return 'text-primary';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return 'AlertTriangle';
      case 'high': return 'AlertCircle';
      case 'medium': return 'Clock';
      case 'low': return 'Info';
      default: return 'Circle';
    }
  };

  const getDepartmentIcon = (department) => {
    switch (department?.toLowerCase()) {
      case 'tax': return 'Calculator';
      case 'hr/payroll': return 'Users';
      case 'finance': return 'DollarSign';
      case 'accounting': return 'FileText';
      case 'executive': return 'Crown';
      default: return 'Building';
    }
  };

  const getUrgencyStatus = (daysUntil) => {
    if (daysUntil < 0) return { label: 'Overdue', color: 'text-error', bg: 'bg-error/10' };
    if (daysUntil === 0) return { label: 'Due Today', color: 'text-error', bg: 'bg-error/10' };
    if (daysUntil <= 3) return { label: 'Due Soon', color: 'text-warning', bg: 'bg-warning/10' };
    if (daysUntil <= 7) return { label: 'This Week', color: 'text-primary', bg: 'bg-primary/10' };
    return { label: 'Upcoming', color: 'text-muted-foreground', bg: 'bg-muted/10' };
  };

  const getFilteredDeadlines = () => {
    let filtered = deadlines;

    // Filter by status
    if (filter !== 'all') {
      if (filter === 'overdue') {
        filtered = filtered?.filter(deadline => 
          deadline?.status === 'overdue' || getDaysUntilDeadline(deadline?.deadline) < 0
        );
      } else {
        filtered = filtered?.filter(deadline => deadline?.status === filter);
      }
    }

    // Sort deadlines
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          return (priorityOrder?.[b?.priority] || 0) - (priorityOrder?.[a?.priority] || 0);
        case 'status':
          return a?.status?.localeCompare(b?.status);
        default:
          return new Date(a.deadline) - new Date(b.deadline);
      }
    });

    return filtered;
  };

  const handleUpdateStatus = (deadlineId, newStatus) => {
    console.log(`Updating deadline ${deadlineId} status to ${newStatus}`);
    // Implement status update functionality
  };

  const handleGenerateReport = (deadlineId) => {
    console.log(`Generating compliance report for deadline ${deadlineId}`);
    // Navigate to report generator with compliance template
  };

  const handleSetReminder = (deadlineId) => {
    console.log(`Setting reminder for deadline ${deadlineId}`);
    // Implement reminder functionality
  };

  const filteredDeadlines = getFilteredDeadlines();
  const overdueCount = deadlines?.filter(d => 
    d?.status === 'overdue' || getDaysUntilDeadline(d?.deadline) < 0
  )?.length;
  const dueSoonCount = deadlines?.filter(d => {
    const days = getDaysUntilDeadline(d?.deadline);
    return days >= 0 && days <= 7 && d?.status !== 'completed';
  })?.length;
  const completedCount = deadlines?.filter(d => d?.status === 'completed')?.length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{overdueCount}</div>
              <div className="text-sm text-muted-foreground">Overdue</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{dueSoonCount}</div>
              <div className="text-sm text-muted-foreground">Due Soon</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{completedCount}</div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{deadlines?.length}</div>
              <div className="text-sm text-muted-foreground">Total Tasks</div>
            </div>
          </div>
        </div>
      </div>
      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'all' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              All ({deadlines?.length})
            </button>
            <button
              onClick={() => setFilter('overdue')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'overdue' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Overdue ({overdueCount})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'pending' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'completed' 
                  ? 'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Completed ({completedCount})
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="deadline">Sort by Deadline</option>
            <option value="priority">Sort by Priority</option>
            <option value="status">Sort by Status</option>
          </select>

          <Button variant="outline" iconName="Bell" size="sm">
            Notifications
          </Button>
        </div>
      </div>
      {/* Deadlines List */}
      <div className="bg-card border border-border rounded-lg card-shadow">
        {filteredDeadlines?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">All Caught Up!</h4>
            <p className="text-muted-foreground">
              {filter === 'all' ? 'No compliance tasks found' : `No ${filter} compliance tasks`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredDeadlines?.map((deadline) => {
              const daysUntil = getDaysUntilDeadline(deadline?.deadline);
              const urgency = getUrgencyStatus(daysUntil);
              
              return (
                <div key={deadline?.id} className="p-6 hover:bg-muted/30 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getStatusBackground(deadline?.status)}`}>
                        <Icon name={getDepartmentIcon(deadline?.department)} size={24} className={getStatusColor(deadline?.status)} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-lg font-semibold text-foreground">{deadline?.task}</h4>
                          <div className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${urgency?.bg} ${urgency?.color}`}>
                            <Icon name="Calendar" size={12} />
                            <span>{urgency?.label}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-muted-foreground mb-3">
                          <div className="flex items-center space-x-1">
                            <Icon name="Calendar" size={14} />
                            <span>Due: {formatDate(deadline?.deadline)}</span>
                            {daysUntil >= 0 && (
                              <span className="text-xs">
                                ({daysUntil} day{daysUntil !== 1 ? 's' : ''} left)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="Building" size={14} />
                            <span>{deadline?.department}</span>
                          </div>
                          <div className={`flex items-center space-x-1 ${getPriorityColor(deadline?.priority)}`}>
                            <Icon name={getPriorityIcon(deadline?.priority)} size={14} />
                            <span className="capitalize">{deadline?.priority} Priority</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusBackground(deadline?.status)} ${getStatusColor(deadline?.status)}`}>
                            {deadline?.status?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      {deadline?.status !== 'completed' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            iconName="FileText"
                            onClick={() => handleGenerateReport(deadline?.id)}
                          >
                            Generate
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            iconName="Bell"
                            onClick={() => handleSetReminder(deadline?.id)}
                          >
                            Remind
                          </Button>
                        </>
                      )}
                      
                      {deadline?.status === 'in-progress' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          iconName="CheckCircle"
                          onClick={() => handleUpdateStatus(deadline?.id, 'completed')}
                          className="text-success"
                        >
                          Complete
                        </Button>
                      )}
                      
                      {deadline?.status === 'not-started' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          iconName="Play"
                          onClick={() => handleUpdateStatus(deadline?.id, 'in-progress')}
                          className="text-primary"
                        >
                          Start
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceDeadlines;